import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { getUserRole } from '../utils/roleHierarchy';
import { filterEntitiesByAccess } from '../utils/assignmentSystem';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import '../styles/CreateIdeas.css';

const CreateIdeas = ({ user }) => {
  const modal = useModal();
  // Get selected brand from global context
  const { selectedBrand: contextBrand, selectedOrg: contextOrg } = useOrgBrand();

  const [formData, setFormData] = useState({
    title: '',
    synopsis: '',
    brand: '',
    contentType: 'social',
    length: 'medium',
    keyPoints: ['']
  });

  const [availableBrands, setAvailableBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const userRole = getUserRole(user);

  useEffect(() => {
    fetchBrands();
  }, []);

  // Auto-fill brand from context when brands are loaded
  useEffect(() => {
    if (availableBrands.length > 0 && contextBrand && !formData.brand) {
      const matchingBrand = availableBrands.find(b =>
        (b.id === contextBrand._id || b.id === contextBrand.id) ||
        (b._id === contextBrand._id || b._id === contextBrand.id)
      );
      if (matchingBrand) {
        setFormData(prev => ({
          ...prev,
          brand: matchingBrand.id || matchingBrand._id
        }));
      }
    }
  }, [availableBrands, contextBrand]);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const brandsResponse = await axios.get('/api/brands/list');
      const brands = brandsResponse.data.brands || [];

      // Filter brands based on user role and assignments
      let accessibleBrands = brands;
      if (userRole === 'operator') {
        const assignmentsResponse = await axios.get('/api/user/assignments');
        const userAssignments = assignmentsResponse.data.assignments;
        accessibleBrands = filterEntitiesByAccess(brands, userRole, userAssignments, 'brands');
      }

      // Sort brands alphabetically by name
      const sortedBrands = accessibleBrands.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setAvailableBrands(sortedBrands);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleKeyPointChange = (index, value) => {
    setFormData(prev => {
      const newKeyPoints = [...prev.keyPoints];
      newKeyPoints[index] = value;
      return { ...prev, keyPoints: newKeyPoints };
    });
  };

  const addKeyPoint = () => {
    setFormData(prev => ({
      ...prev,
      keyPoints: [...prev.keyPoints, '']
    }));
  };

  const removeKeyPoint = (index) => {
    setFormData(prev => ({
      ...prev,
      keyPoints: prev.keyPoints.filter((_, i) => i !== index)
    }));
  };

  const handleReset = () => {
    let brandId = '';
    if (contextBrand) {
      const foundBrand = availableBrands.find(b =>
        (b.id === contextBrand._id || b.id === contextBrand.id) ||
        (b._id === contextBrand._id || b._id === contextBrand.id)
      );
      brandId = (foundBrand && foundBrand.id) || contextBrand._id || '';
    }

    setFormData({
      title: '',
      synopsis: '',
      brand: brandId,
      contentType: 'social',
      length: 'medium',
      keyPoints: ['']
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.brand) {
      modal.warning('Please select a brand');
      return;
    }

    if (!formData.title.trim()) {
      modal.warning('Please enter a title');
      return;
    }

    try {
      setSubmitting(true);

      // Get the selected brand to find its organization_id
      const selectedBrand = availableBrands.find(b => b.id === formData.brand || b._id === formData.brand);
      if (!selectedBrand || !selectedBrand.organization_id) {
        modal.warning('Please select a valid brand');
        return;
      }

      // Filter out empty key points
      const filteredKeyPoints = formData.keyPoints.filter(point => point.trim() !== '');

      // Create the idea payload
      const ideaData = {
        brand_id: formData.brand,
        organization_id: selectedBrand.organization_id,
        ideas: [{
          title: formData.title,
          synopsis: formData.synopsis,
          content_type: formData.contentType,
          length: formData.length,
          key_points: filteredKeyPoints,
          status: 'pending_review' // Starts in review state for operators
        }]
      };

      await axios.post('/api/ideas', ideaData, { withCredentials: true });

      modal.success('Idea created successfully! You can review it in the Review Ideas panel.');
      handleReset();

    } catch (error) {
      console.error('Error creating idea:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Failed to create idea';
      modal.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="super-admin-internal-layout create-ideas-panel">
      <nav className="internal-nav">
        <div className="internal-nav-items">
          <button type="button" className="internal-nav-btn active">
            Create New Idea
          </button>
        </div>
      </nav>
      <div className="internal-content">
        <p className="creation-subtitle">Manually create a content idea to review and send to clients</p>

        <form className="create-ideas-form" onSubmit={handleSubmit}>
          <div className="form-columns">
            <div className="form-column form-column-meta">
              <div className="form-group">
                <label htmlFor="brand">Brand *</label>
                <div className="brand-display" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-surface-light-muted)',
                  border: '1px solid var(--color-border-light-strong)',
                  borderRadius: '4px',
                  cursor: 'not-allowed'
                }}>
                  {(() => {
                    const selectedBrandObj = availableBrands.find(b => (b.id || b._id) === formData.brand);
                    return (
                      <>
                        {selectedBrandObj?.icon_url ? (
                          <BrandIcon
                            src={selectedBrandObj.icon_url}
                            alt=""
                            style={{ width: '24px', height: '24px', borderRadius: '4px' }}
                          />
                        ) : (
                          <Icon name="tag" size="1.5rem" />
                        )}
                        <span style={{ fontWeight: '500' }}>
                          {selectedBrandObj?.name || 'No brand selected'}
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="form-group">
                <label>Content Type</label>
                <p className="field-description">Select the content type for this idea</p>
                <select
                  value={formData.contentType}
                  onChange={(e) => setFormData(prev => ({ ...prev, contentType: e.target.value }))}
                  className="content-type-select"
                >
                  <option value="social">Social</option>
                  <option value="blog">Blog</option>
                </select>
              </div>

              <div className="form-group">
                <label>Length</label>
                <p className="field-description">Select the target length for this idea's content</p>
                <select
                  value={formData.length}
                  onChange={(e) => setFormData(prev => ({ ...prev, length: e.target.value }))}
                  className="content-type-select"
                >
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                </select>
              </div>
            </div>

            <div className="form-column form-column-content">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter idea title..."
                  required
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                />
              </div>

              <div className="form-group">
                <label htmlFor="synopsis">Synopsis / Description *</label>
                <textarea
                  id="synopsis"
                  name="synopsis"
                  value={formData.synopsis}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Describe the content idea in detail..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Key Points (Optional)</label>
                <p className="field-description">Add important points or talking points for this idea</p>
                <div className="key-points-container">
                  {formData.keyPoints.map((point, index) => (
                    <div key={index} className="key-point-row">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => handleKeyPointChange(index, e.target.value)}
                        placeholder={`Key point ${index + 1}...`}
                        autoComplete="off"
                        data-1p-ignore="true"
                        data-lpignore="true"
                        data-bwignore="true"
                        data-form-type="other"
                      />
                      {formData.keyPoints.length > 1 && (
                        <button
                          type="button"
                          className="remove-point-btn"
                          onClick={() => removeKeyPoint(index)}
                          title="Remove this point"
                        >
                          <Icon name="cancel" size="1.25rem" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="add-point-btn"
                    onClick={addKeyPoint}
                  >
                    <Icon name="plus" size="1rem" />
                    Add Key Point
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-lg btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button type="submit" className="btn-lg btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Icon name="spin6 animate-spin" size="1rem" />
                  Creating Idea...
                </>
              ) : (
                <>
                  <Icon name="lightbulb" size="1rem" />
                  Create Idea
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateIdeas;
